import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import { getAccountId, getWhiteLabelPublisherId, isAdvertiser } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'

import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '../services/configuration/campaign-permissions'

/**
 * Gate do fluxo novo de briefing offsite (opt-in por publisher). Fail closed:
 * sem publisher no contexto, com permissão pendente, com erro ou sem a chave na
 * resposta, o fluxo fica oculto.
 *
 * Devolve o `publisherId` já resolvido para que quem consome o gate escope suas
 * próprias requisições no mesmo publisher, sem repetir a regra de white label.
 */
type AllowOffsiteBriefingCampaign = {
  enabled: boolean
  publisherId: string | null
  isPending: boolean
  /** Home sem publisher. Só fora de white label, com o app-info já resolvido. */
  homeInbox: boolean
  /** Home ou fila escopada. A aba e a tabela usam o mesmo valor. */
  canFetchQueue: boolean
}

export function useAllowOffsiteBriefingCampaign(): AllowOffsiteBriefingCampaign {
  const searchParams = useQueryParams()
  const {
    isWhiteLabel,
    isPending: isAppInfoPending,
    isError: isAppInfoError
  } = useAppInfo()
  const rmid = searchParams.get('rmid')

  const publisherId = useMemo(() => {
    // Sem o app-info, `isWhiteLabel` é false mesmo em host white label —
    // resolver o rmid agora mostraria a fila (ou o empty) do publisher
    // errado, e sem rmid o empty "nenhuma campanha" piscava antes da lista.
    if (isAppInfoPending && !isWhiteLabel) return null

    if (isWhiteLabel && isAdvertiser()) {
      return getWhiteLabelPublisherId() ?? null
    }

    return rmid
  }, [isAppInfoPending, isWhiteLabel, rmid])

  const requestParams = { publisher_id: publisherId }

  const { data, isSuccess, isPending } = useQuery({
    enabled: !!publisherId,
    /**
     * A conta entra na key porque o `QueryClient` é de módulo e a troca de
     * conta reinicia só o Redux. Sem isso, dentro do `staleTime` a segunda
     * conta reusaria a permissão da primeira para o mesmo publisher.
     */
    queryKey: [getCampaignPermissionsServiceKey, getAccountId(), requestParams],
    queryFn: async () => {
      const response = await getCampaignPermissionsService(requestParams)

      return response.data
    },
    staleTime: 5 * 60 * 1000
  })

  const enabled =
    !!publisherId && isSuccess && data?.allow_offsite_briefing_campaign === true

  // Query desabilitada sem publisher também tem status pending no TanStack;
  // isso não representa uma permissão em carregamento. App-info pendente
  // sim: ainda não dá para decidir rmid vs token.
  const pending =
    (!!isAppInfoPending && !isWhiteLabel) || (!!publisherId && isPending)

  // Erro de /app-info deixa isWhiteLabel false. Sem este corte, um host
  // white label sem cache cairia na fila da home, misturando publishers.
  const homeInbox = !isWhiteLabel && !isAppInfoError && !pending && !publisherId

  return {
    enabled,
    publisherId,
    isPending: pending,
    homeInbox,
    canFetchQueue: homeInbox || enabled
  }
}
